// Pet insurance journey.

/** @type {import("../../form/types").JourneyConfig} */
const pet = {
  key: "pet",
  label: "Pet Insurance",

  // The exact value redux stores in `insuranceType`; the route guard
  // restores it from here after a refresh or a deep link.
  insuranceType: "Pet Insurance",
  entry: "/pet-landing",
  landing: "landing:pet",
  caseIdPrefix: "P",

  rail: {
    greeting: "Hi there",
    image: "/assets/images/cartoon-dog.png",
    bullets: [
      "Instant policies online",
      "Flexible payment options",
      "Insurers you can trust",
      "24/7 claims support",
    ],
  },

  steps: [
    {
      key: "pet-type",
      summary: ["pet_type"],
      label: "Pet type",
      path: "/pet-planning",
      mobileTitle: "Your pet",
      rail: { title: "what kind of", accent: "pet?" },
      hideCta: true,
      form: {
        id: "pet-species",
        sections: [
          {
            fields: [
              {
                name: "pet_type",
                type: "choice",
                label: "What kind of pet are you insuring?",
                required: true,
                col: 12,
                props: { illustrated: true },
                options: {
                  source: "reference",
                  domain: "pet-species",
                  // Artwork and copy per canonical code; the catalogue itself
                  // stays a registry of codes.
                  decorate: {
                    DOG: {
                      subtitle: "Canine cover",
                      image: "/assets/images/dog-radio-icon.png",
                      badge: "Most popular",
                      perks: [
                        "Vet fees & emergency treatment",
                        "Third-party liability cover",
                        "Microchip & lost-pet recovery",
                      ],
                    },
                    CAT: {
                      subtitle: "Feline cover",
                      image: "/assets/images/cat-radio-icon.png",
                      badge: "Indoor friendly",
                      perks: [
                        "Vet fees & illness cover",
                        "Indoor accident & emergency cover",
                        "Microchip & lost-pet recovery",
                      ],
                    },
                  },
                },
              },
            ],
          },
        ],
      },
    },

    {
      key: "your-pet",
      summary: ["pet_name", "pet_breed"],
      label: "Your pet",
      path: "/pet-details",
      mobileTitle: "Pet details",
      rail: {
        title: "tell us about your",
        accent: "pet",
        subtitle: "Breed, age and health history decide which plans are available.",
      },
      cta: "Continue to your details",
      form: {
        id: "pet-details",
        sections: [
          {
            fields: [
              { name: "pet_name", type: "text", label: "Pet's name", required: true, help: "What you call them at home" },
              {
                name: "pet_gender",
                type: "radio",
                label: "Gender",
                required: true,
                options: { source: "reference", domain: "pet-gender" },
              },
              {
                name: "pet_dob",
                type: "date",
                label: "Date of birth",
                required: true,
                // The pet provider declines animals under 8 weeks or over 10 years.
                rules: [{ rule: "age", min: 0, max: 10, message: "We can only cover pets up to 10 years old" }],
                props: { maxDate: "today" },
              },
              {
                name: "breed_type",
                type: "select",
                label: "Breed group",
                required: true,
                options: {
                  source: "reference",
                  domain: "pet-breed-group",
                  dependsOn: "pet_type",
                },
              },
              {
                name: "pet_breed",
                type: "select",
                label: "Breed",
                required: true,
                options: {
                  source: "reference",
                  domain: "pet-breed",
                  // Both, because group names repeat across species — "Pedigree"
                  // exists for cats and dogs and means a different list.
                  dependsOn: ["pet_type", "breed_type"],
                },
                props: {
                  emptyText: "No breeds listed for this group",
                  emptyHint: "Please choose a different breed group.",
                },
              },
            ],
          },
          {
            title: "Health",
            fields: [
              {
                name: "is_microchipped",
                type: "radio",
                label: "Is your pet microchipped?",
                required: true,
                options: [
                  { value: "yes", label: "Yes" },
                  { value: "no", label: "No" },
                ],
              },
              {
                name: "microchip_number",
                type: "text",
                label: "Microchip number",
                help: "15 digits, on the vet's microchip paperwork",
                visibleWhen: { field: "is_microchipped", equals: "yes" },
                requiredWhen: { field: "is_microchipped", equals: "yes" },
                rules: [{ rule: "length", min: 9, max: 15 }],
              },
              {
                name: "is_neutered",
                type: "radio",
                label: "Is your pet neutered or spayed?",
                required: true,
                options: [
                  { value: "yes", label: "Yes" },
                  { value: "no", label: "No" },
                ],
              },
              {
                name: "is_pre_existing_condition",
                type: "radio",
                label: "Any pre-existing conditions?",
                required: true,
                col: 12,
                options: [
                  { value: "yes", label: "Yes" },
                  { value: "no", label: "No" },
                ],
              },
              {
                name: "pre_existing_condition_details",
                type: "textarea",
                label: "Tell us about the condition",
                help: "Diagnosis, when it started, and any ongoing treatment",
                col: 12,
                visibleWhen: { field: "is_pre_existing_condition", equals: "yes" },
                requiredWhen: { field: "is_pre_existing_condition", equals: "yes" },
              },
            ],
          },
        ],
      },
    },

    {
      key: "documents",
      label: "Documents",
      path: "/document-upload",
      screen: "document-upload",
      mobileTitle: "Your documents",
      rail: {
        title: "upload your",
        accent: "documents",
        subtitle:
          "We read your Emirates ID automatically so you don't have to type it in.",
      },
      cta: "Continue to your details",
    },

    {
      key: "your-details",
      summary: ["first_name", "emirate"],
      label: "Your details",
      path: "/owner-details",
      mobileTitle: "Owner details",
      rail: { title: "tell us about", accent: "yourself" },
      cta: "See your quotes",
      form: {
        id: "pet-owner",
        sections: [
          {
            fields: [
              {
                name: "title",
                type: "select",
                label: "Title",
                col: 3,
                required: true,
                options: { source: "reference", domain: "title" },
              },
              {
                name: "first_name",
                type: "text",
                label: "First name",
                help: "Exactly as printed on your Emirates ID",
                col: 4,
                required: true,
                ocrKeys: "Name",
                ocrTransform: "firstName",
              },
              {
                name: "middle_name",
                type: "text",
                label: "Middle name",
                help: "Leave blank if you don't have one",
                col: 5,
                ocrKeys: "Name",
                ocrTransform: "middleName",
              },
              {
                name: "last_name",
                type: "text",
                label: "Last name",
                help: "Your family name",
                required: true,
                ocrKeys: "Name",
                ocrTransform: "lastName",
              },
              {
                name: "dob",
                type: "date",
                label: "Date of birth",
                required: true,
                rules: [{ rule: "age", min: 18, message: "Policyholders must be 18 or older" }],
                ocrKeys: "Date_of_Birth",
                props: { maxDate: "today" },
              },
              {
                name: "gender",
                type: "select",
                label: "Gender",
                required: true,
                options: { source: "reference", domain: "gender" },
                ocrKeys: "Sex",
              },
              {
                name: "nationality",
                type: "select",
                label: "Nationality",
                required: true,
                options: { source: "reference", domain: "nationality" },
                ocrKeys: "Nationality",
              },
              {
                name: "emirates_id",
                type: "text",
                label: "Emirates ID",
                help: "15 digits from the front of the card",
                placeholder: "784-YYYY-XXXXXXX-X",
                required: true,
                rules: ["emiratesId"],
                ocrKeys: "ID_Number",
              },
              { name: "email", type: "email", label: "Email", required: true, rules: ["email"], placeholder: "name@example.com", help: "Your policy documents are sent here" },
              { name: "mobile", type: "phone", label: "Mobile number", required: true, rules: ["uaeMobile"] },
            ],
          },
          {
            title: "Address",
            fields: [
              { name: "address_1", type: "text", label: "Address line 1", col: 12, required: true, help: "Building name or number, and street" },
              { name: "address_2", type: "text", label: "Address line 2", col: 12, help: "Area or district" },
              {
                name: "emirate",
                type: "select",
                label: "Emirate",
                required: true,
                options: { source: "reference", domain: "emirate" },
              },
            ],
          },
          {
            title: "Payment",
            fields: [
              {
                name: "payment_frequency",
                type: "radio",
                label: "How would you like to pay?",
                required: true,
                col: 12,
                options: { source: "reference", domain: "payment-frequency" },
              },
              { name: "agency_reference", type: "text", label: "Agency reference", help: "Optional — for broker records" },
            ],
          },
        ],
      },
    },

    {
      key: "choose-plan",
      label: "Choose plan",
      path: "/pet-quote-details",
      screen: "quote-details",
      screenProps: { product: "pet" },
      aliasPaths: [{ path: "/pet-quote-compare", screen: "quote-compare" }],
      layout: "wide",
      mobileTitle: "Choose your plan",
      rail: { title: "choose your", accent: "plan" },
      hideCta: true,
    },

    {
      key: "review-pay",
      label: "Review & pay",
      path: "/pet-plan-summary",
      screen: "plan-summary",
      screenProps: { product: "pet" },
      layout: "wide",
      mobileTitle: "Review & pay",
      rail: { title: "one last", accent: "step" },
      cta: "Confirm & purchase",
    },
  ],
};

export default pet;
